import { Component, Input, Output, EventEmitter } from '@angular/core';
import { EventResponse } from '../../models/response.module';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-event-card',
  standalone: false,
  templateUrl: './event-card.html',
  styleUrl: './event-card.css'
})
export class EventCard {
  @Input() event: EventResponse;
  @Input() showDelete: boolean = false;
  @Output() deleteRequested = new EventEmitter<EventResponse>();

  constructor(private router: Router, private translate: TranslateService) {}

  onDeleteClick() {
    this.deleteRequested.emit(this.event);
  }

  navigateToManageEvent(eventId: number) {
    this.router.navigate([`company-profile/manage-event/${eventId}`]);
  }

  isEventUpcoming(): boolean {
    if (!this.event.start_date) return false;
    const startDate = new Date(this.event.start_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return startDate >= today;
  }

  formatDateDMYDots(dateInput: string | Date | null | undefined): string {
    if (!dateInput) return '';
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  getGoogleMapsUrl(): string {
    const address = this.event.address || '';
    const city = this.event.city || '';
    const country = this.event.country || '';
    const query = encodeURIComponent(`${address}, ${city}, ${country}`.trim());
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  }

  getCategoryDisplay(): string {
    // Use category if available, otherwise fall back to event type
    if (this.event.category) {
      switch (this.event.category) {
        case 'competition': return this.translate.instant('eventCard.category.competition');
        case 'training': return this.translate.instant('eventCard.category.training');
        case 'social': return this.translate.instant('eventCard.category.social');
        default: return this.translate.instant('eventCard.category.event');
      }
    }
    // Legacy fallback based on event_type_id
    if (this.event.event_type_id === 0) return this.translate.instant('eventCard.category.competition');
    if (this.event.event_type_id === 2) return this.translate.instant('eventCard.category.training');
    if (this.event.event_type_id === 1) return this.translate.instant('eventCard.category.social');
    // Final fallback to event type name
    return this.event.event_type?.event_type_name || this.translate.instant('eventCard.category.event');
  }

  getCategoryBadgeClass(): string {
    // Use category if available
    if (this.event.category) {
      switch (this.event.category) {
        case 'competition': return 'badge-competition';
        case 'training': return 'badge-training';
        case 'social': return 'badge-social';
        default: return 'badge-light-primary';
      }
    }
    // Legacy fallback based on event_type_id
    if (this.event.event_type_id === 0) return 'badge-competition';
    if (this.event.event_type_id === 2) return 'badge-training';
    if (this.event.event_type_id === 1) return 'badge-social';
    return 'badge-light-primary';
  }

  navigateToOrganisation(event: Event) {
    event.stopPropagation();
    if (this.event.company?.company_id) {
      this.router.navigate(['/organisations', this.event.company.company_id]);
    }
  }
}
